import React, {useState, useEffect} from 'react';
import {
    Users,
    UserPlus,
    TrendingUp,
    Calendar,
    AlertTriangle,
    Loader2,
    Book,
    MessageSquare,
    Bot,
    Database,
    HardDrive,
    CheckCircle,
    Clock,
    CreditCard
} from 'lucide-react';

const DashboardPage = () => {
    const [userStats, setUserStats] = useState({
        total_users: 0,
        new_users_last_month: 0,
        monthly_growth: '',
        new_users_last_week: 0,
        weekly_growth: '',
        new_users_last_day: 0
    });

    const [systemStats, setSystemStats] = useState(null);
    const [booksCount, setBooksCount] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            const accessToken = localStorage.getItem('accessToken');

            try {
                // Fetch user stats
                const userResponse = await fetch('https://api.digitalaalim.com/admin/user-stats/', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                });

                if (!userResponse.ok) {
                    throw new Error('Failed to fetch user statistics');
                }

                const userData = await userResponse.json();
                setUserStats(userData);

                // Fetch system stats
                const systemResponse = await fetch('https://api.digitalaalim.com/admin/system-stats', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                });

                if (systemResponse.ok) {
                    const systemData = await systemResponse.json();
                    setSystemStats(systemData);
                }

                // Fetch books count
                const booksResponse = await fetch('https://api.digitalaalim.com/admin/books-count', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                });

                if (booksResponse.ok) {
                    const booksData = await booksResponse.json();
                    setBooksCount(booksData);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-600" size={36}/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex items-center">
                    <AlertTriangle className="text-red-500 mr-3" size={24}/>
                    <div>
                        <h3 className="text-red-800 font-medium">Error loading dashboard</h3>
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    const statCards = [
        {
            title: 'Total Users',
            value: userStats.total_users,
            icon: <Users size={32}/>,
            color: 'bg-blue-500',
        },
        {
            title: 'New Users (Month)',
            value: userStats.new_users_last_month,
            subtext: userStats.monthly_growth,
            icon: <UserPlus size={32}/>,
            color: 'bg-emerald-500',
        },
        {
            title: 'Monthly Growth',
            value: userStats.monthly_growth,
            icon: <TrendingUp size={32}/>,
            color: 'bg-purple-500',
        },
        {
            title: 'New Users (Week)',
            value: userStats.new_users_last_week,
            subtext: userStats.weekly_growth,
            icon: <Calendar size={32}/>,
            color: 'bg-amber-500',
        },
        {
            title: 'Weekly Growth',
            value: userStats.weekly_growth,
            icon: <TrendingUp size={32}/>,
            color: 'bg-rose-500',
        },
        {
            title: 'New Users (Today)',
            value: userStats.new_users_last_day,
            icon: <UserPlus size={32}/>,
            color: 'bg-indigo-500',
        }
    ];

    return (
        <div className="space-y-8">
            <div>
                <h1 className="text-3xl font-bold text-gray-800">Digital Aalim Admin Dashboard</h1>
                <p className="mt-2 text-gray-600">
                    Your ultimate solution for Islamic text retrieval and chatbot training
                </p>
            </div>

            {/* Quick Stats Overview */}
            {systemStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-sm font-medium">Total Users</p>
                                <p className="text-3xl font-bold mt-2">{systemStats.users?.total || 0}</p>
                            </div>
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <Users size={28}/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-100 text-sm font-medium">Total Books</p>
                                <p className="text-3xl font-bold mt-2">{booksCount?.total || 0}</p>
                            </div>
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <Book size={28}/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-purple-100 text-sm font-medium">Total Queries</p>
                                <p className="text-3xl font-bold mt-2">
                                    {(systemStats.queries?.total_aalim || 0) + (systemStats.queries?.total_qalam || 0)}
                                </p>
                            </div>
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <MessageSquare size={28}/>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-orange-100 text-sm font-medium">Madrasah Bots</p>
                                <p className="text-3xl font-bold mt-2">{systemStats.madrasah_bots?.total || 0}</p>
                            </div>
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <Bot size={28}/>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* User Growth Statistics */}
            <div className="p-6 bg-white rounded-xl shadow-sm">
                <h2 className="text-xl font-semibold text-gray-700 mb-6">User Growth Statistics</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {statCards.map((card, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                        >
                            <div className="flex p-6">
                                <div className={`${card.color} text-white p-4 rounded-lg`}>
                                    {card.icon}
                                </div>
                                <div className="ml-4">
                                    <h3 className="text-sm font-medium text-gray-500">{card.title}</h3>
                                    <p className="text-2xl font-bold text-gray-800">{card.value}</p>
                                    {card.subtext && (
                                        <p className="text-sm text-gray-500">{card.subtext}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* System Statistics by Sect */}
            {systemStats && (
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Users by Sect */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <Users size={20} className="mr-2 text-blue-600"/>
                            Users by Sect
                        </h3>
                        <div className="space-y-3">
                            {Object.entries(systemStats.users?.by_sect || {}).map(([sect, count]) => (
                                <div key={sect} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{sect}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-32 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-blue-600 h-2 rounded-full"
                                                style={{
                                                    width: `${(count / (systemStats.users?.total || 1)) * 100}%`
                                                }}
                                            />
                                        </div>
                                        <span className="text-sm font-bold text-gray-900 w-12 text-right">{count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Books by Sect */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <Book size={20} className="mr-2 text-green-600"/>
                            Books by Sect
                        </h3>
                        <div className="space-y-3">
                            {booksCount && Object.entries(booksCount.by_sect || {}).map(([sect, count]) => (
                                <div key={sect} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{sect}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="w-32 bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-green-600 h-2 rounded-full"
                                                style={{
                                                    width: `${(count / (booksCount.total || 1)) * 100}%`
                                                }}
                                            />
                                        </div>
                                        <span className="text-sm font-bold text-gray-900 w-12 text-right">{count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {booksCount?.note && (
                            <p className="text-xs text-gray-500 mt-4 italic">{booksCount.note}</p>
                        )}
                    </div>
                </div>
            )}

            {/* Queries and Bots Statistics */}
            {systemStats && (
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Queries by Sect */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <MessageSquare size={20} className="mr-2 text-purple-600"/>
                            Queries by Sect
                        </h3>
                        <div className="mb-4 grid grid-cols-2 gap-4">
                            <div className="bg-purple-50 rounded-lg p-3">
                                <p className="text-xs text-purple-600 font-medium">Digital Aalim</p>
                                <p className="text-2xl font-bold text-purple-900">{systemStats.queries?.total_aalim || 0}</p>
                            </div>
                            <div className="bg-indigo-50 rounded-lg p-3">
                                <p className="text-xs text-indigo-600 font-medium">Digital Qalam</p>
                                <p className="text-2xl font-bold text-indigo-900">{systemStats.queries?.total_qalam || 0}</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {Object.entries(systemStats.queries?.by_sect || {}).map(([sect, count]) => (
                                <div key={sect} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{sect}</span>
                                    <span className="text-sm font-bold text-gray-900">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Madrasah Bots */}
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                            <Bot size={20} className="mr-2 text-orange-600"/>
                            Madrasah Bots
                        </h3>
                        <div className="mb-4 grid grid-cols-2 gap-4">
                            <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-xs text-green-600 font-medium flex items-center">
                                    <CheckCircle size={12} className="mr-1"/>
                                    Approved
                                </p>
                                <p className="text-2xl font-bold text-green-900">{systemStats.madrasah_bots?.approved || 0}</p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-3">
                                <p className="text-xs text-yellow-600 font-medium flex items-center">
                                    <Clock size={12} className="mr-1"/>
                                    Pending
                                </p>
                                <p className="text-2xl font-bold text-yellow-900">{systemStats.madrasah_bots?.pending || 0}</p>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {Object.entries(systemStats.madrasah_bots?.by_sect || {}).map(([sect, data]) => (
                                <div key={sect} className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700">{sect}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-green-600">{data.approved} approved</span>
                                        <span className="text-sm font-bold text-gray-900">{data.total} total</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Users by Plan */}
            {systemStats && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <CreditCard size={20} className="mr-2 text-indigo-600"/>
                        Users by Subscription Plan
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(systemStats.users?.by_plan || {}).map(([plan, count]) => {
                            const colors = {
                                FREE: 'bg-gray-100 text-gray-800 border-gray-300',
                                BASIC: 'bg-blue-100 text-blue-800 border-blue-300',
                                PREMIUM: 'bg-purple-100 text-purple-800 border-purple-300'
                            };
                            return (
                                <div key={plan} className={`rounded-lg p-4 border-2 ${colors[plan] || colors.FREE}`}>
                                    <p className="text-sm font-medium">{plan}</p>
                                    <p className="text-3xl font-bold mt-2">{count}</p>
                                    <p className="text-xs mt-1">
                                        {((count / (systemStats.users?.total || 1)) * 100).toFixed(1)}% of users
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Vector Store Information */}
            {systemStats?.vector_stores && !systemStats.vector_stores.error && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <Database size={20} className="mr-2 text-cyan-600"/>
                        Vector Store Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-cyan-50 rounded-lg p-4">
                            <p className="text-sm text-cyan-600 font-medium">Total Size</p>
                            <p className="text-2xl font-bold text-cyan-900">{systemStats.vector_stores.total_size}</p>
                        </div>
                        <div className="bg-cyan-50 rounded-lg p-4">
                            <p className="text-sm text-cyan-600 font-medium">Index Count</p>
                            <p className="text-2xl font-bold text-cyan-900">{systemStats.vector_stores.count}</p>
                        </div>
                    </div>
                    <div className="mt-4 space-y-2">
                        {Object.entries(systemStats.vector_stores).map(([key, value]) => {
                            if (key !== 'total_size' && key !== 'count') {
                                return (
                                    <div key={key} className="flex items-center justify-between text-sm">
                                        <span className="text-gray-600 font-mono">{key}</span>
                                        <span className="text-gray-900 font-medium flex items-center">
                                            <HardDrive size={14} className="mr-1 text-gray-400"/>
                                            {value}
                                        </span>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            )}

            {/* About Section */}
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl shadow-sm border border-blue-100">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">About Digital Aalim</h2>
                <p className="text-gray-700 leading-relaxed">
                    Embark on a journey of Islamic knowledge with the Digital Aalim Dashboard, a
                    ground-breaking platform tailored to streamline the retrieval of Islamic text from any source.
                    Designed specifically for Islamic content, this innovative tool harnesses
                    the power of AI to extract text from audio recordings, OCR scans, and websites,
                    empowering users to access invaluable Islamic teachings with unparalleled ease.
                </p>
            </div>
        </div>
    );
};

export default DashboardPage;